'use client';

import { cn } from '@/lib/utils';
import { useSearchChatCoordination } from '@/utils/search-chat-coordination';
import {
  createConciergeClient,
  type ConciergeSource,
} from '@contentstack/agents-sdk';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { AskAiSparkleIcon } from '../Icons/Icons';
import { AskAiChatBody, AskAiChatHeader } from './ask-ai-chat-panel';
import type { ChatMessage, MessageFeedback } from './ask-ai-types';


// Configuration constants
const API_ENDPOINT = '/academy/api/ask-ai';
const STORAGE_USER_ID = 'ask-ai:userId';
const STORAGE_TRANSCRIPT = 'ask-ai:transcript:v1:';
const MAX_MESSAGES = 100;
const SAVE_DELAY = 400;
const SCROLL_THROTTLE_MS = 50;

// Utility functions
const getPanelWidth = (expanded: boolean, inline = false) => {
  const width = expanded ? 520 : 420;
  const reserve = inline ? 280 : expanded ? 64 : 32;
  const maxWidth = inline ? '70vw' : '80vw';
  return `min(${width}px, ${maxWidth}, calc(100vw - ${reserve}px))`;
};

const getStorageKey = (userId: string) => STORAGE_TRANSCRIPT + userId;

const trimMessages = (messages: ChatMessage[]) =>
  messages.length > MAX_MESSAGES ? messages.slice(-MAX_MESSAGES) : messages;

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    // Handle common error patterns
    if (error.includes('404') || error.includes('Not Found')) {
      return 'The AI service is currently unavailable. Please check your configuration or try again later.';
    }
    if (error.includes('401') || error.includes('403') || error.includes('Unauthorized')) {
      return 'Authentication failed. Please check your API credentials.';
    }
    if (error.includes('500') || error.includes('Internal Server Error')) {
      return 'The AI service encountered an error. Please try again in a moment.';
    }
    if (error.includes('Network') || error.includes('fetch')) {
      return 'Network error occurred. Please check your connection and try again.';
    }
    // Filter out raw JS errors and chunk information
    if (error.includes('chunk') || error.includes('.js') || error.includes('webpack')) {
      return 'A technical error occurred. Please refresh the page and try again.';
    }
    return error;
  }
  
  if (error && typeof error === 'object') {
    const err = error as any;
    if (err.message) {
      return getErrorMessage(err.message);
    }
    if (err.status === 404) {
      return 'The AI service is currently unavailable. Please check your configuration or try again later.';
    }
    if (err.status >= 400 && err.status < 500) {
      return 'Authentication or request error. Please try again.';
    }
    if (err.status >= 500) {
      return 'The AI service encountered an error. Please try again in a moment.';
    }
  }
  
  return 'Something went wrong. Please try again.';
};

// Data parsing utilities
const parser = {
  source: (value: unknown): ConciergeSource | null => {
    if (!value || typeof value !== 'object') return null;
    const o = value as Record<string, unknown>;
    if (typeof o.key !== 'string' || typeof o.value !== 'string') return null;
    return {
      key: o.key,
      value: o.value,
      ...(typeof o.contentType === 'string' && { contentType: o.contentType }),
    };
  },

  transcript: (
    data: unknown,
  ): { threadId: string | null; messages: ChatMessage[] } | null => {
    if (!data || typeof data !== 'object') return null;
    const o = data as Record<string, unknown>;
    if (!Array.isArray(o.messages)) return null;

    const messages: ChatMessage[] = o.messages
      .filter(
        (item): item is Record<string, unknown> =>
          item &&
          typeof item === 'object' &&
          typeof item.id === 'string' &&
          (item.role === 'user' || item.role === 'assistant') &&
          typeof item.content === 'string',
      )
      .map((m) => {
        const message: ChatMessage = {
          id: m.id as string,
          role: m.role as 'user' | 'assistant',
          content: m.content as string,
        };
        if (Array.isArray(m.sources)) {
          const sources = m.sources
            .map(parser.source)
            .filter(Boolean) as ConciergeSource[];
          if (sources.length) message.sources = sources;
        }
        if (m.feedback) {
          message.feedback = m.feedback as MessageFeedback;
        }
        return message;
      })
      .filter((msg) => msg.role !== 'assistant' || msg.content.trim() !== '');

    return {
      threadId: typeof o.threadId === 'string' ? o.threadId : null,
      messages,
    };
  },
};

// Component types and interfaces
interface AskAiProps {
  className?: string;
  title?: string;
  placeholder?: string;
}

type AskAiContextValue = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

// Context and hooks
export const AskAiContext = createContext<AskAiContextValue | null>(null);


// UI Components
const askAiPillBaseClass =
  'pointer-events-auto inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-border-medium font-inter transition-[background-color,box-shadow] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-amethyst-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1';

function AskAiPillContent({ iconClassName }: { iconClassName?: string }) {
  return (
    <div className="flex items-center justify-start gap-2.5">
      <div className="justify-start font-['Inter'] text-sm leading-5 font-normal text-text-default-light transition-colors hover:text-text-default-strong">
        Ask AI
      </div>
      <div className="relative size-4">
        <AskAiSparkleIcon className={cn('size-4', iconClassName)} />
      </div>
    </div>
  );
}

export function AskAiHeaderButton({ className }: { className?: string }) {
  const ctx = useContext(AskAiContext);
  const buttonRef = useRef<HTMLButtonElement>(null);

  if (!ctx) return null;
  const { isOpen, setIsOpen } = ctx;

  useEffect(() => {
    const element = buttonRef.current;
    const glowElement = element?.previousElementSibling as HTMLElement;
    if (!element || !glowElement) return;

    let angle = 0;
    let animationId = 0;
    const rotate = () => {
      angle = (angle + 0.5) % 360; // Slower increment for subtle animation
      element.style.setProperty('--angle', `${angle}deg`);
      glowElement.style.setProperty('--angle', `${angle}deg`);
      animationId = requestAnimationFrame(rotate);
    };

    animationId = requestAnimationFrame(rotate);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="relative">
      {/* Outer glow effect */}
      <div
        className="absolute inset-[-2px] rounded-full opacity-10"
        style={
          {
            background: `
                linear-gradient(#292828, #292828) padding-box,
                linear-gradient(
                  var(--angle, 0deg),
                  transparent 0%,
                  #A7F3D0 30%,
                  #818CF8 50%,
                  #A78BFA 70%,
                  transparent 100%
                ) border-box
              `,
            filter: 'blur(4px)',
            '--angle': '0deg',
          } as React.CSSProperties & { '--angle': string }
        }
      />

      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }}
        className={cn(
          // Base styles with animated gradient
          'relative z-10 inline-flex h-10 max-w-80 items-center justify-start gap-2.5 rounded-[100px] py-3 pr-4 pl-6',
          // Interactive styles
          'transition-colors duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-amethyst-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
          // Custom Ask AI button styling
          'ask-ai-button',
          className,
        )}
        style={
          {
            '--angle': '0deg',
            background: `
            linear-gradient(var(--color-surface-3), var(--color-surface-3)) padding-box,
            linear-gradient(
              var(--angle, 0deg),
              transparent 0%,
              #A7F3D0 30%,
              #818CF8 50%,
              #A78BFA 70%,
              transparent 100%
            ) border-box
          `,
            border: '1px solid transparent',
          } as React.CSSProperties & { '--angle': string }
        }
        aria-label={isOpen ? 'Close chat' : 'Open Ask AI chat'}
        aria-expanded={isOpen}
      >
        <AskAiPillContent />
      </button>
    </div>
  );
}


export type AskAiProviderProps = AskAiProps & {
  children?: ReactNode;
  showFloatingTrigger?: boolean;
  panelPlacement?: 'overlay' | 'inline';
};

// Simplified message utilities
const updateMessage = (
  messages: ChatMessage[],
  id: string,
  updates: Partial<ChatMessage>,
) => {
  const index = messages.findIndex((m) => m.id === id);
  if (index === -1) return messages;
  
  return [
    ...messages.slice(0, index),
    { ...messages[index], ...updates },
    ...messages.slice(index + 1),
  ];
};

const addSources = (
  messages: ChatMessage[],
  id: string,
  newSources: ConciergeSource[],
) => {
  const index = messages.findIndex((m) => m.id === id && m.role === 'assistant');
  if (index === -1) return messages;

  const existing = messages[index].sources || [];
  const existingKeys = new Set(existing.map((s) => `${s.key}:${s.value}`));
  const uniqueSources = newSources.filter((s) => !existingKeys.has(`${s.key}:${s.value}`));

  return updateMessage(messages, id, {
    sources: [...existing, ...uniqueSources],
  });
};

const extractEventSources = (event: unknown): ConciergeSource[] => {
  if (!event || typeof event !== 'object') return [];
  
  // Only extract from 'sources' key, ignore deprecated 'results' key
  const eventObj = event as { sources?: unknown; results?: unknown };
  const maybeSources = eventObj.sources;
  if (!Array.isArray(maybeSources)) return [];
  return maybeSources
    .map(parser.source)
    .filter((source): source is ConciergeSource => source !== null);
};

// Simplified streaming handler
function createStreamHandler(
  messageId: string,
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>,
  onContentUpdate?: () => void,
) {
  let content = '';

  const updateContent = (newContent: string) => {
    content = newContent;
    setMessages((prev) =>
      updateMessage(prev, messageId, { content }),
    );
    onContentUpdate?.();
  };

  return {
    append: (delta: string) => updateContent(content + delta),
    replace: (newContent: string) => updateContent(newContent),
    getContent: () => content,
  };
}

// Main Provider Component
export function AskAiProvider({
  children,
  showFloatingTrigger = true,
  panelPlacement = 'overlay',
  className,
  placeholder = 'Ask a follow up question ...',
}: AskAiProviderProps) {
  const conciergeClient = useMemo(
    () => createConciergeClient({ endpoint: API_ENDPOINT }),
    [],
  );

  // State
  const [isOpen, setIsOpen] = useState(false);
  
  // Coordination for search-chat behavior
  const coordination = useSearchChatCoordination();


  // Refs
  const cancelRef = useRef<(() => void) | null>(null);
  const userIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const autoScrollEnabledRef = useRef(true);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [sourcesExpanded, setSourcesExpanded] = useState<
    Record<string, boolean>
  >({});
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);


  // Set up coordination listeners for search-chat behavior
  useEffect(() => {
    // Listen for search opened - close chat if it's open
    const unsubscribeSearchOpened = coordination.subscribe('search-opened', () => {
      if (isOpen) {
        coordination.setChatWasOpenBeforeSearch(true);
        setIsOpen(false);
      }
    });

    // Listen for search closed - restore chat if it was open before
    const unsubscribeSearchClosed = coordination.subscribe('search-closed', () => {
      if (coordination.getChatWasOpenBeforeSearch()) {
        setIsOpen(true);
        coordination.setChatWasOpenBeforeSearch(false);
      }
    });

    return () => {
      unsubscribeSearchOpened();
      unsubscribeSearchClosed();
    };
  }, [isOpen, coordination]);

  // Initialize user and load transcript
  useEffect(() => {
    const initializeUser = () => {
      const ls = globalThis.localStorage;
      const stored = ls?.getItem(STORAGE_USER_ID) || '';
      const id = stored.trim() || crypto.randomUUID();

      try {
        ls?.setItem(STORAGE_USER_ID, id);
        const transcriptData = ls?.getItem(getStorageKey(id));
        if (transcriptData) {
          const parsed = parser.transcript(JSON.parse(transcriptData));
          if (parsed) {
            setThreadId(parsed.threadId);
            setMessages(parsed.messages);
          }
        }
      } catch {
        // Ignore storage errors
      }

      userIdRef.current = id;
      setUserId(id);
    };

    initializeUser();
    return () => {
      if (cancelRef.current) {
        cancelRef.current();
      }
    };
  }, []);

  // Auto-save transcript
  useEffect(() => {
    if (!userId || !userIdRef.current) return;

    const saveTranscript = () => {
      try {
        const ls = globalThis.localStorage;
        if (!ls) return;

        ls.setItem(
          getStorageKey(userIdRef.current!),
          JSON.stringify({
            threadId,
            messages: trimMessages(messages),
          }),
        );
      } catch {
        // Ignore storage errors
      }
    };

    const timeoutId = setTimeout(saveTranscript, SAVE_DELAY);
    return () => clearTimeout(timeoutId);
  }, [messages, threadId, userId]);

  // Computed values (moved here to be available for useEffect)
  const lastMessage = messages[messages.length - 1];
  const showLoadingDots =
    isLoading && (!lastMessage || lastMessage.content === '');
  const streamingMessageId = isLoading ? lastMessage?.id : null;

  // Simplified UI and scroll management
  useEffect(() => {
    if (!isOpen) {
      setIsPanelExpanded(false);
      document.body.style.overflowX = '';
      return;
    }

    document.body.style.overflowX = 'hidden';

    // Auto-scroll to bottom on message changes
    const scrollToBottom = (smooth = false) => {
      messagesEndRef.current?.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
        inline: 'nearest'
      });
    };

    const scrollId = requestAnimationFrame(() => scrollToBottom(!isLoading));

    // Focus input when not loading
    const focusId = !isLoading 
      ? requestAnimationFrame(() => {
          const input = formRef.current?.querySelector<HTMLInputElement>('input');
          input?.focus({ preventScroll: true });
        })
      : null;

    return () => {
      cancelAnimationFrame(scrollId);
      if (focusId) cancelAnimationFrame(focusId);
      document.body.style.overflowX = '';
    };
  }, [isOpen, isLoading, messages.length]);

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    const prompt = inputValue.trim();
    if (!prompt || isLoading || !userId) return;

    if (cancelRef.current) {
      cancelRef.current();
    }
    const assistantId = crypto.randomUUID();

    // Add messages immediately
    flushSync(() => {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'user', content: prompt },
        { id: assistantId, role: 'assistant', content: '' },
      ]);
      setInputValue('');
      setIsLoading(true);
    });

    // Simple throttled scroll for streaming
    let scrollTimeout: NodeJS.Timeout | null = null;
    const scrollToBottom = () => {
      if (!autoScrollEnabledRef.current || scrollTimeout) return;
      
      scrollTimeout = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'auto',
          block: 'end',
          inline: 'nearest'
        });
        scrollTimeout = null;
      }, SCROLL_THROTTLE_MS);
    };

    const streamHandler = createStreamHandler(assistantId, setMessages, scrollToBottom);

    try {
      const run = conciergeClient.streamText({
        prompt,
        ...(threadId && { threadId }),
        userId,
      });
      cancelRef.current = run.cancel;

      let hasReceivedContent = false;

      for await (const event of run.events) {
        switch (event.type) {
          case 'text':
            hasReceivedContent = true;
            streamHandler.append(event.textDelta);
            break;
          case 'sources': {
            const sourcesFromEvent = extractEventSources(event);
            if (sourcesFromEvent.length > 0) {
              setMessages((prev) =>
                addSources(prev, assistantId, sourcesFromEvent),
              );
            }
            break;
          }
          case 'metadata': {
            const sourcesFromMetadata = extractEventSources(event);
            if (sourcesFromMetadata.length > 0) {
              setMessages((prev) =>
                addSources(prev, assistantId, sourcesFromMetadata),
              );
            }
            break;
          }
          case 'done':
            const doneSources = extractEventSources(event);
            setThreadId(event.threadId);
            setUserId(event.userId);
            const finalContent = event.text || streamHandler.getContent();
            setMessages((prev) =>
              updateMessage(prev, assistantId, {
                content: finalContent,
                ...(doneSources.length > 0 && { sources: doneSources }),
              }),
            );
            
            // Final smooth scroll when streaming is complete
            requestAnimationFrame(() => {
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({
                  behavior: 'smooth',
                  block: 'end',
                  inline: 'nearest'
                });
              }
            });
            break;
          case 'error':
            console.error('Ask AI Stream Error:', event);
            const errorMessage = getErrorMessage(event.message);
            streamHandler.replace(errorMessage);
            break;
        }
      }

      // Ensure we have content
      if (!hasReceivedContent && !streamHandler.getContent()) {
        streamHandler.replace('No response received. Please try again.');
      }
    } catch (error) {
      console.error('Ask AI Error:', error);
      const errorMessage = getErrorMessage(error);
      streamHandler.replace(errorMessage);
    } finally {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      cancelRef.current = null;
      setIsLoading(false);
    }
  };

  const floatingFabStyle = useMemo(
    () => ({
      right: isOpen
        ? `calc(${getPanelWidth(isPanelExpanded)} + 1.5rem)`
        : 'max(1.5rem, env(safe-area-inset-right))',
      bottom: 'max(1.5rem, env(safe-area-inset-bottom))',
    }),
    [isOpen, isPanelExpanded],
  );

  // Event handlers
  const toggleSourcesExpanded = (messageId: string) => {
    setSourcesExpanded((prev) => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  const handleNewChat = () => {
    if (cancelRef.current) {
      cancelRef.current();
    }
    cancelRef.current = null;
    setMessages([]);
    setThreadId(null);
    setInputValue('');
    setIsLoading(false);
    setSourcesExpanded({});
  };

  const contextValue = useMemo(() => ({ isOpen, setIsOpen }), [isOpen]);

  const chatPanel = (
    <div
      className="ask-ai-panel flex h-full min-h-0 w-full flex-1 flex-col border-l border-border-medium bg-surface-1"
      data-ask-ai-component="chat-panel"
    >
      <AskAiChatHeader
        onNewChat={handleNewChat}
        onClose={() => setIsOpen(false)}
        panelExpanded={isPanelExpanded}
        onTogglePanelExpand={() => setIsPanelExpanded((prev) => !prev)}
      />
      <AskAiChatBody
        messages={messages}
        isLoading={isLoading}
        showLoadingDots={showLoadingDots}
        sourcesExpandedByMessageId={sourcesExpanded}
        onToggleSources={toggleSourcesExpanded}
        streamingMessageId={streamingMessageId}
        messagesEndRef={messagesEndRef}
        chatScrollRef={chatScrollRef}
        autoScrollEnabledRef={autoScrollEnabledRef}
        formRef={formRef}
        onSubmit={() => void handleSubmit()}
        inputValue={inputValue}
        onInputChange={setInputValue}
        placeholder={placeholder}
        submitDisabled={!inputValue.trim() || isLoading || !userId}
      />
    </div>
  );

  if (panelPlacement === 'inline') {
    return (
      <AskAiContext.Provider value={contextValue}>
        <div className={cn('flex min-h-dvh w-full min-w-0 flex-row relative', className)}>
          <div 
            className="flex min-h-dvh min-w-0 flex-1 flex-col ask-ai-content-container"
            style={{
              '--panel-width': isOpen ? getPanelWidth(isPanelExpanded, true) : '0px',
              transition: 'margin-right 300ms ease-out',
            } as React.CSSProperties & Record<string, string>}
          >
            {children}
          </div>
          <div
            className={cn(
              'fixed top-0 right-0 z-[1] hidden h-dvh flex-col overflow-hidden transform bg-white shadow-lg desktop:flex',
              !isOpen && 'pointer-events-none',
            )}
            style={{ 
              width: getPanelWidth(isPanelExpanded, true),
              transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 300ms ease-out, width 300ms ease-out',
            }}
            role="region"
            aria-label="Ask AI chat"
            aria-hidden={!isOpen}
          >
            {chatPanel}
          </div>
        </div>
      </AskAiContext.Provider>
    );
  }

  return (
    <AskAiContext.Provider value={contextValue}>
      {children}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-[9998] flex hidden flex-col items-end overflow-hidden lg:flex',
          isOpen ? 'pointer-events-auto' : 'pointer-events-none',
          className,
        )}
      >
        {/* Backdrop with blur effect - Click to close */}
        <div
          className={cn(
            'fixed inset-0 bg-black/40 backdrop-blur-sm cursor-pointer',
            isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
          style={{
            transition: 'opacity 300ms ease-out, backdrop-filter 300ms ease-out',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
          aria-hidden
          aria-label="Close chat panel"
        />

        {/* Chat Panel */}
        <div
          className={cn(
            'fixed right-0 top-0 z-10 h-dvh overflow-hidden bg-surface-1 shadow-2xl',
            isOpen
              ? 'pointer-events-auto'
              : 'pointer-events-none',
          )}
          style={{ 
            width: getPanelWidth(isPanelExpanded),
            maxWidth: '80vw',
            transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
            transition: 'transform 300ms ease-out, width 250ms ease-out',
          }}
          onClick={(e) => e.stopPropagation()}
          aria-hidden={!isOpen}
        >
          {chatPanel}
        </div>

        {/* Floating Trigger */}
        {showFloatingTrigger && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsOpen((prev) => !prev);
            }}
            style={{
              ...floatingFabStyle,
              transition: 'all 300ms ease-out',
              backgroundColor: 'var(--color-surface-3)',
            }}
            className={cn(
              askAiPillBaseClass,
              'fixed z-[9999] hidden min-h-12 px-4 py-2.5 lg:block',
              'ask-ai-button',
              isOpen && 'is-open',
            )}
            aria-label={isOpen ? 'Close chat' : 'Open Ask AI chat'}
            aria-expanded={isOpen}
          >
            <AskAiPillContent iconClassName="size-[22px] sm:size-6" />
          </button>
        )}
      </div>
    </AskAiContext.Provider>
  );
}

export default function AskAi(props: AskAiProps) {
  return <AskAiProvider {...props} showFloatingTrigger />;
}
